import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { shipficiencyLogo } from "@src/branding/shipficiencyLogo";
import { strings } from "@src/i18n/strings";

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f0f4f8 0%, #e8edf2 100%)",
  padding: "2rem 1.5rem",
  textAlign: "center",
  fontFamily:
    '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
};
const imageContainerStyle: React.CSSProperties = {
  width: "180px",
  height: "180px",
  marginBottom: "1.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const imageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  borderRadius: "12px",
  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))",
};
const headingStyle: React.CSSProperties = {
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "#1a2b3c",
  margin: "0 0 0.5rem 0",
  lineHeight: 1.3,
};
const subTextStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  color: "#5a6a7a",
  margin: "0 0 1rem 0",
  lineHeight: 1.5,
  maxWidth: "280px",
};
const noteStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#5a6a7a",
  margin: "0 0 1.5rem 0",
  lineHeight: 1.5,
  maxWidth: "300px",
  fontStyle: "italic",
};
const buttonStyle: React.CSSProperties = {
  padding: "0.65rem 2rem",
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#ffffff",
  backgroundColor: "#037bff",
  border: "none",
  borderRadius: "24px",
  cursor: "pointer",
  transition: "background-color 0.2s ease, transform 0.1s ease",
  boxShadow: "0 2px 8px rgba(3, 123, 255, 0.3)",
};
const buttonHoverStyle: React.CSSProperties = {
  backgroundColor: "#0b5fb8",
  transform: "translateY(-1px)",
};
const timerStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#8a9aaa",
  marginTop: "1rem",
};
const dotContainerStyle: React.CSSProperties = {
  display: "flex",
  gap: "6px",
  justifyContent: "center",
  marginBottom: "0.75rem",
};
const AUTO_REFRESH_SECONDS = 15;
const processingStrings = strings.components.processingMessage;

interface ProcessingMessageProps {
  onRefresh?: () => void;
}

export default function ProcessingMessage({ onRefresh }: ProcessingMessageProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS);

  useEffect(() => {
    if (!onRefresh) return;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onRefresh();
          return AUTO_REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onRefresh]);
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
      setCountdown(AUTO_REFRESH_SECONDS);
    }
  }, [onRefresh]);

  return (
    <div style={containerStyle}>
      <div style={imageContainerStyle}>
        <img src={shipficiencyLogo} alt={processingStrings.imageAlt} style={imageStyle} />
      </div>

      <div style={dotContainerStyle}>
        <span className="processing-dot processing-dot--1" />
        <span className="processing-dot processing-dot--2" />
        <span className="processing-dot processing-dot--3" />
      </div>

      <h2 style={headingStyle}>
        {processingStrings.headingLine1}
        <br />
        {processingStrings.headingLine2}
      </h2>

      <p style={subTextStyle}>{processingStrings.body}</p>

      <p style={noteStyle}>
        {processingStrings.notePrefix}
        <strong>{processingStrings.noteHighlight}</strong>
        {processingStrings.noteSuffix}
      </p>

      {onRefresh && (
        <button
          type="button"
          style={isHovered ? { ...buttonStyle, ...buttonHoverStyle } : buttonStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleRefresh}
        >
          {processingStrings.refreshNow}
        </button>
      )}

      {onRefresh && <span style={timerStyle}>{processingStrings.autoRefreshIn(countdown)}</span>}
    </div>
  );
}
